import { Component, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

// EZ
import { EzTextService, TimeZone } from '../../ez-text.service';
import { EzFormInputType, InputConfig, InputSelectOption } from '../../../types';
import { UiLibraryModule } from "@template-ui/template-ui.module";
import { DocMeta } from "@template-ui/decorators";


type TemplatePreset = {
  id: string;
  label: string;
  template: string;
  context: Record<string, unknown>;
};

@DocMeta({
  showcaseTitle: 'Text template playground',
  showcaseDesc: 'Type a template + JSON context to see the rendered result.',
  hide: true
})
@Component({
    selector: 'ui-text-template-demo, ez-text-template-demo',
    imports: [
        CommonModule,
        FormsModule,
        UiLibraryModule
    ],
    templateUrl: './text-template-demo.component.html',
    styleUrl: './text-template-demo.component.scss'
})
export class TextTemplateDemoComponent {
  private readonly defaultTemplate = [
    'Hello {{name | smartTitleCase}}!',
    'Your balance is {{amount | currency(USD)}}.',
    'Report is due {{dueDate | yyyy-MM-dd}}.',
    'Today is {{currentDate | MMMM d, yyyy}}.'
  ].join('\n');

  private readonly defaultContext = {
    name: "jane o'malley",
    amount: 1234.56,
    dueDate: '2025-11-13T15:30:00Z',
    currentDate: new Date().toISOString()
  };

  readonly presets: TemplatePreset[] = [
    {
      id: 'paymentReminder',
      label: 'Payment reminder',
      template: this.defaultTemplate,
      context: this.defaultContext
    },
    {
      id: 'eventInvite',
      label: 'Event invitation',
      template: [
        'Hi {{recipient | smartTitleCase}},',
        'You are invited to {{eventName}} on {{eventDate | MMMM d, yyyy}} at {{location}}.',
        'Please RSVP by {{rsvpBy | MMM d}}.'
      ].join('\n'),
      context: {
        recipient: 'alex smith',
        eventName: 'Annual Recognition Night',
        eventDate: '2025-12-05T18:00:00Z',
        rsvpBy: '2025-11-28T17:00:00Z',
        location: 'Board Room 3A'
      }
    },
    {
      id: 'caseUpdate',
      label: 'Case status update',
      template: [
        'Case {{caseNumber}} is currently {{status | titleCase}}.',
        'Investigator: {{investigator | smartTitleCase}}.',
        'Last updated on {{updatedOn | MMM d, yyyy h:mm a}}.'
      ].join('\n'),
      context: {
        caseNumber: 'INV-2025-12345',
        status: 'pending review',
        investigator: "riley o'reilly",
        updatedOn: '2025-11-17T08:15:00Z'
      }
    },
    {
      id: 'timezoneDemo',
      label: 'Timezone opt-in formatting',
      template: [
        'Reference instant (UTC): {{ releaseTime | isoDateTimeZ }}',
        'No adjustment (default): {{ releaseTime | yyyy-MM-dd HH:mm }}',
        "Viewer local override: {{ releaseTime | local('yyyy-MM-dd HH:mm') }}",
        `Los Angeles (tz): {{ releaseTime | tz('${TimeZone.LosAngeles}',"MMM d, yyyy h:mm a") }}`,
        `Berlin (tz): {{ releaseTime | tz('${TimeZone.Berlin}',"MMM d, yyyy h:mm a") }}`,
        `Force UTC display: {{ releaseTime | utc("MMM d, yyyy HH:mm 'UTC'") }}`
      ].join('\n'),
      context: {
        releaseTime: '2025-01-05T18:00:00Z'
      }
    },
    {
      id: 'weekYearContrast',
      label: 'Calendar vs ISO week year',
      template: [
        'Reference date: {{ weekDate | yyyy-MM-dd }}',
        'Calendar year (yyyy): {{ weekDate | yyyy }}',
        'ISO week-based year (YYYY): {{ weekDate | YYYY }}',
        'ISO week number (ww): {{ weekDate | ww }}'
      ].join('\n'),
      context: {
        weekDate: '2021-01-01T00:00:00Z'
      }
    }
  ];

  formConfig: InputConfig[] = [
    {
      type: EzFormInputType.SELECT,
      name: "presets",
      label: "Sample preset",
      options: this.presets.map(p => {
        return {viewValue: p.label, value: p} as InputSelectOption;
      }),

    } as InputConfig,
    {
      name: "reset",
      type: EzFormInputType.BUTTON,
      buttonText: "Reset to defaults",
    } as InputConfig
  ];

  readonly selectedPresetId = signal(this.presets[0]?.id ?? '');

  readonly presetOptions = this.presets.map(preset => ({
    label: preset.label,
    value: preset.id
  }));

  templateInput = signal(this.defaultTemplate);
  contextInput = signal(JSON.stringify(this.defaultContext, null, 2));
  parseError = signal<string | null>(null);
  contextObject = signal<Record<string, unknown>>(this.cloneContext(this.defaultContext));

  renderedOutput = computed(() =>
    this.ezText.resolveTemplate(
      this.templateInput(),
      this.contextObject()
    )
  );

  readonly ezText = inject(EzTextService);

  onPresetChange(e: { name: string; value: any }): void {
    const presetId = e.value.id;
    const preset = this.presets.find(item => item.id === presetId);
    if (!preset) {
      return;
    }

    this.selectedPresetId.set(presetId);
    this.applyPreset(preset);
  }

  resetToDefaults(): void {
    const preset = this.presets[0];
    if (!preset) {
      return;
    }

    this.selectedPresetId.set(preset.id);
    this.applyPreset(preset);
  }

  onTemplateChange(value: string): void {
    this.templateInput.set(value);
  }

  onContextChange(value: string): void {
    this.contextInput.set(value);

    try {
      const parsed = JSON.parse(value);
      this.contextObject.set(parsed);
      this.parseError.set(null);
    } catch (error) {
      this.parseError.set(String(error));
    }
  }

  private applyPreset(preset: TemplatePreset): void {
    const safeContext = this.cloneContext(preset.context);

    this.templateInput.set(preset.template);
    this.contextInput.set(JSON.stringify(safeContext, null, 2));
    this.contextObject.set(safeContext);
    this.parseError.set(null);
  }

  private cloneContext<T extends Record<string, unknown>>(context: T): T {
    if (typeof structuredClone === 'function') {
      return structuredClone(context);
    }
    return JSON.parse(JSON.stringify(context));
  }
}